import React, { useCallback, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LoadingSkeleton } from '@/components/ui/loading-spinner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  User, 
  Users, 
  Check, 
  ChevronsUpDown, 
  X, 
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/core/utils';
import {
  useUnifiedEmployeeSelection,
  EmployeeSelectionConfig,
  BaseEmployeeSelection,
  StandardEmployeeSelection,
} from '@/hooks/employees/useUnifiedEmployeeSelection';

// Component configuration for different UI modes
export interface UnifiedEmployeeSelectProps<T extends BaseEmployeeSelection = StandardEmployeeSelection> {
  // Required props
  businessId: string | null;
  value?: string | string[];
  onValueChange?: (value: string | string[] | null) => void;
  
  // Selection configuration
  config?: Partial<EmployeeSelectionConfig>;
  dataFormat?: 'base' | 'standard' | 'communication' | 'advanced';
  
  // UI configuration
  variant?: 'simple' | 'command' | 'checkbox-list' | 'compact';
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  
  // Styling
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  triggerStyle?: React.CSSProperties;
  
  // Advanced features
  showAvailabilityIndicator?: boolean;
  availabilityCheckConfig?: {
    startTime?: Date;
    endTime?: Date;
    existingShifts?: any[];
    excludeShiftId?: string;
  };
  showRoleInfo?: boolean;
  showStatusBadge?: boolean;
  
  // Event handlers
  onSelectionChange?: (employees: T[]) => void;
  onSearch?: (term: string) => void;
  onOpenChange?: (open: boolean) => void;
  
  // Custom rendering
  renderEmployee?: (employee: T, isSelected: boolean) => React.ReactNode;
  renderTrigger?: (selectedEmployees: T[], placeholder: string) => React.ReactNode;
}

// Availability indicator component
const AvailabilityIndicator: React.FC<{
  available: boolean;
  conflicts?: any[];
  refreshing?: boolean;
}> = ({ available, conflicts, refreshing }) => {
  const Icon = available ? CheckCircle : AlertCircle;
  const color = available ? 'text-green-500' : 'text-red-500';
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative">
            <Icon className={cn('h-3 w-3', color)} />
            {refreshing && (
              <Clock className="absolute -top-0.5 -right-0.5 h-2 w-2 text-blue-500 animate-pulse" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {available ? (
            <div>
              <p>Available for selected time</p>
              {refreshing && <p className="text-xs text-blue-300">Refreshing...</p>}
            </div>
          ) : (
            <div>
              <p className="font-medium">Conflicts detected:</p>
              {conflicts?.map((conflict, idx) => (
                <p key={idx} className="text-xs">
                  {conflict.title} ({conflict.startTime} - {conflict.endTime})
                </p>
              )) || <p className="text-xs">Time conflict exists</p>}
              {refreshing && <p className="text-xs text-blue-300">Refreshing...</p>}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Employee display component
const EmployeeDisplay: React.FC<{
  employee: StandardEmployeeSelection;
  isSelected: boolean;
  showRoleInfo: boolean;
  showStatusBadge: boolean;
  showAvailabilityIndicator: boolean;
  available?: boolean;
  conflicts?: any[];
  refreshing?: boolean;
}> = ({ 
  employee, 
  isSelected, 
  showRoleInfo, 
  showStatusBadge, 
  showAvailabilityIndicator,
  available = true,
  conflicts,
  refreshing = false
}) => {
  
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {showAvailabilityIndicator && (
          <AvailabilityIndicator available={available} conflicts={conflicts} refreshing={refreshing} />
        )}
        <div className="flex items-center gap-2 min-w-0">
          <User className="h-4 w-4 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate">{employee.name}</p>
            {showRoleInfo && employee.roleInfo?.name && (
              <p className="text-xs text-muted-foreground truncate">
                {employee.roleInfo.name}
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-1 flex-shrink-0">
        {showStatusBadge && (employee.contract_type || employee.employment_unified_type) && (
          <Badge
            variant="secondary"
            className="text-xs"
          >
            {employee.contract_type || employee.employment_unified_type}
          </Badge>
        )}
        {isSelected && <Check className="h-4 w-4 text-primary" />}
      </div>
    </div>
  );
};

/**
 * Unified employee select component that handles all employee selection patterns
 * with configurable UI modes and business logic
 */
export function UnifiedEmployeeSelect<T extends BaseEmployeeSelection = StandardEmployeeSelection>({
  businessId,
  value,
  onValueChange,
  config = {},
  dataFormat = 'standard',
  variant = 'simple',
  placeholder = 'Select employee...',
  emptyMessage = 'No employees found',
  disabled = false,
  className,
  triggerClassName,
  contentClassName,
  triggerStyle,
  showAvailabilityIndicator = false,
  availabilityCheckConfig,
  showRoleInfo = false,
  showStatusBadge = false,
  onSelectionChange,
  onSearch,
  onOpenChange,
  renderEmployee,
  renderTrigger,
}: UnifiedEmployeeSelectProps<T>) {
  
  // Use unified employee selection hook
  const {
    employees,
    filteredEmployees,
    selectedEmployees,
    loading,
    error,
    searchTerm,
    setSelectedEmployees,
    toggleEmployeeSelection,
    clearSelection,
    selectAll,
    setSearchTerm,
    isEmployeeSelected,
    getSelectedEmployeesData,
  } = useUnifiedEmployeeSelection<T>(businessId, config, dataFormat);

  // Sync internal selection with external value
  React.useEffect(() => {
    if (value !== undefined) {
      const normalizedValue = Array.isArray(value) ? value : (value ? [value] : []);
      if (JSON.stringify(normalizedValue.sort()) !== JSON.stringify(selectedEmployees.sort())) {
        setSelectedEmployees(normalizedValue);
      }
    }
  }, [value, selectedEmployees, setSelectedEmployees]);

  // Handle selection changes
  const handleSelectionChange = useCallback((employeeId: string) => {
    toggleEmployeeSelection(employeeId);
    
    const newSelection = isEmployeeSelected(employeeId)
      ? selectedEmployees.filter(id => id !== employeeId)
      : config.mode === 'single' 
        ? [employeeId] 
        : [...selectedEmployees, employeeId];
    
    // Call external handlers
    if (onValueChange) {
      if (config.mode === 'single') {
        onValueChange(newSelection.length > 0 ? newSelection[0] : null);
      } else {
        onValueChange(newSelection);
      }
    }
    
    if (onSelectionChange) {
      const selectedData = employees.filter(emp => newSelection.includes(emp.id));
      onSelectionChange(selectedData);
    }
  }, [
    toggleEmployeeSelection, 
    isEmployeeSelected, 
    selectedEmployees, 
    config.mode,
    onValueChange, 
    onSelectionChange, 
    employees
  ]);

  // Handle search
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    if (onSearch) {
      onSearch(term);
    }
  }, [setSearchTerm, onSearch]);

  // Get selected employees data
  const selectedEmployeesData = useMemo(() => {
    return getSelectedEmployeesData();
  }, [getSelectedEmployeesData]);

  // Availability computation state
  const [availabilityResults, setAvailabilityResults] = React.useState<Record<string, { isAvailable: boolean; conflicts: any[] }>>({});
  const [loadingAvailability, setLoadingAvailability] = React.useState(false);
  const [refreshingAvailability, setRefreshingAvailability] = React.useState(false);

  // Import cache service
  const [availabilityCache] = React.useState(() => {
    return import('@/lib/employees/EmployeeAvailabilityCache').then(module => module.employeeAvailabilityCache);
  });

  // Smart availability computation using cache
  const computeAvailability = React.useCallback(async (forceRefresh: boolean = false) => {
    if (!showAvailabilityIndicator || !availabilityCheckConfig || !availabilityCheckConfig.startTime || !availabilityCheckConfig.endTime) {
      setAvailabilityResults({});
      return;
    }

    const employeeIds = filteredEmployees.map(emp => emp.id);

    if (employeeIds.length === 0) {
      setAvailabilityResults({});
      return;
    }

    try {
      const cache = await availabilityCache;

      if (forceRefresh) {
        setLoadingAvailability(true);
        const result = await cache.getAvailability(
          employeeIds,
          availabilityCheckConfig.startTime,
          availabilityCheckConfig.endTime,
          availabilityCheckConfig.excludeShiftId,
          false // Don't use background refresh on force refresh
        );

        setAvailabilityResults(result.data);
      } else {
        // Try to get cached data first for instant display
        const result = await cache.getAvailability(
          employeeIds,
          availabilityCheckConfig.startTime,
          availabilityCheckConfig.endTime,
          availabilityCheckConfig.excludeShiftId,
          true // Enable background refresh
        );


        if (result.fromCache) {
          setAvailabilityResults(result.data);
          setLoadingAvailability(false);

          if (result.refreshing) {
            setRefreshingAvailability(true);
          }
        } else {
          setLoadingAvailability(true);
          setAvailabilityResults(result.data);
        }
      }


    } catch (error) {
      console.error('Availability check error:', error);
      // Set all employees as unavailable on error
      const errorResults: Record<string, { isAvailable: boolean; conflicts: any[] }> = {};
      employeeIds.forEach(id => {
        errorResults[id] = { isAvailable: false, conflicts: [] };
      });
      setAvailabilityResults(errorResults);
    } finally {
      setLoadingAvailability(false);
      setRefreshingAvailability(false);
    }
  }, [
    showAvailabilityIndicator,
    filteredEmployees,
    availabilityCheckConfig?.startTime?.getTime(),
    availabilityCheckConfig?.endTime?.getTime(),
    availabilityCheckConfig?.excludeShiftId,
    availabilityCache
    // REMOVED: availabilityResults - was causing circular dependency
  ]);

  // Subscribe to cache changes for real-time updates
  React.useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    const setupSubscription = async () => {
      const cache = await availabilityCache;
      unsubscribe = cache.subscribe(() => {
        computeAvailability(false);
      });
    };
    
    setupSubscription();
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [availabilityCache, computeAvailability]);

  // Initial availability computation when dependencies change
  React.useEffect(() => {
    computeAvailability(false);
  }, [computeAvailability]);

  // Handle dropdown open to refresh availability
  const handleOpenChange = React.useCallback((open: boolean) => {
    if (open && showAvailabilityIndicator) {
      computeAvailability(true);
    }
    
    // Call the original onOpenChange handler
    if (onOpenChange) {
      onOpenChange(open);
    }
  }, [onOpenChange, showAvailabilityIndicator, computeAvailability]);

  // Render based on variant
  switch (variant) {
    case 'command':
      return (
        <div className={className}>
          <Popover onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                disabled={disabled}
                className={cn(
                  'w-full justify-between',
                  !selectedEmployees.length && 'text-muted-foreground',
                  triggerClassName
                )}
              >
                {renderTrigger ? (
                  renderTrigger(selectedEmployeesData, placeholder)
                ) : selectedEmployees.length > 0 ? (
                  config.mode === 'single' ? (
                    selectedEmployeesData[0]?.name || placeholder
                  ) : (
                    `${selectedEmployees.length} selected`
                  )
                ) : (
                  placeholder
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className={cn('w-full p-0', contentClassName)}>
              <Command>
                <CommandInput 
                  placeholder="Search employees..." 
                  value={searchTerm}
                  onValueChange={handleSearch}
                />
                <CommandList>
                  <CommandEmpty>
                    {loading ? (
                      <div className="py-6">
                        <LoadingSkeleton variant="form" rows={2} />
                      </div>
                    ) : (
                      emptyMessage
                    )}
                  </CommandEmpty>
                  {config.mode === 'multiple' && (
                    <CommandGroup>
                      <CommandItem onSelect={selectAll}>
                        <Users className="mr-2 h-4 w-4" />
                        Select All ({filteredEmployees.length})
                      </CommandItem>
                      <CommandItem onSelect={clearSelection}>
                        <X className="mr-2 h-4 w-4" />
                        Clear Selection
                      </CommandItem>
                    </CommandGroup>
                  )}
                  <CommandGroup>
                    {filteredEmployees.map((employee) => (
                      <CommandItem
                        key={employee.id}
                        value={employee.id}
                        onSelect={() => handleSelectionChange(employee.id)}
                      >
                        {renderEmployee ? (
                          renderEmployee(employee, isEmployeeSelected(employee.id))
                        ) : (
                          <EmployeeDisplay
                            employee={employee as StandardEmployeeSelection}
                            isSelected={isEmployeeSelected(employee.id)}
                            showRoleInfo={showRoleInfo}
                            showStatusBadge={showStatusBadge}
                            showAvailabilityIndicator={showAvailabilityIndicator}
                            available={loadingAvailability ? true : (availabilityResults[employee.id]?.isAvailable ?? false)}
                            conflicts={availabilityResults[employee.id]?.conflicts ?? []}
                            refreshing={refreshingAvailability}
                          />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      );

    case 'checkbox-list':
      return (
        <div className={cn('space-y-2', className)}>
          {loading ? (
            <div className="py-4">
              <LoadingSkeleton variant="form" rows={3} />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {emptyMessage}
            </p>
          ) : (
            filteredEmployees.map((employee) => (
              <div key={employee.id} className="flex items-center space-x-2">
                <Checkbox
                  id={employee.id}
                  checked={isEmployeeSelected(employee.id)}
                  onCheckedChange={() => handleSelectionChange(employee.id)}
                  disabled={disabled}
                />
                <label
                  htmlFor={employee.id}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1"
                >
                  {renderEmployee ? (
                    renderEmployee(employee, isEmployeeSelected(employee.id))
                  ) : (
                    <EmployeeDisplay
                      employee={employee as StandardEmployeeSelection}
                      isSelected={isEmployeeSelected(employee.id)}
                      showRoleInfo={showRoleInfo}
                      showStatusBadge={showStatusBadge}
                      showAvailabilityIndicator={showAvailabilityIndicator}
                      available={loadingAvailability ? true : (availabilityResults[employee.id]?.isAvailable ?? false)}
                      conflicts={availabilityResults[employee.id]?.conflicts ?? []}
                      refreshing={refreshingAvailability}
                    />
                  )}
                </label>
              </div>
            ))
          )}
        </div>
      );

    case 'compact':
      return (
        <Select
          value={config.mode === 'single' ? (selectedEmployees[0] || 'unassigned') : 'unassigned'}
          onValueChange={(value) => {
            const newValue = value === 'unassigned' ? null : value;
            if (onValueChange) {
              onValueChange(newValue);
            }
          }}
          onOpenChange={handleOpenChange}
          disabled={disabled || loading}
        >
          <SelectPrimitive.Trigger
            className={cn(
              'flex w-auto items-center justify-between rounded-md border border-input px-1 py-0.5 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 dark:border-border dark:text-white',
              triggerClassName
            )}
            style={triggerStyle}
          >
            <SelectValue placeholder={placeholder}>
              {selectedEmployeesData[0]?.name || placeholder}
            </SelectValue>
            <SelectPrimitive.Icon asChild>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectContent className={contentClassName} align="end">
            {loading ? (
              <div className="py-2">
                <LoadingSkeleton variant="form" rows={2} />
              </div>
            ) : (
              <>
                {loadingAvailability && showAvailabilityIndicator && (
                  <div className="py-1.5 px-2 text-xs text-muted-foreground text-center">
                    Checking availability...
                  </div>
                )}
                {config.includeUnassigned !== false && (
                  <SelectItem value="unassigned" hideIndicator className="py-1">
                    <span className="flex items-center gap-1.5 text-sm w-full">
                      <User className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="flex-1 text-right">Unassign</span>
                    </span>
                  </SelectItem>
                )}
                {filteredEmployees.map((employee) => {
                  const isAvailable = loadingAvailability
                    ? true  // Show as available during loading (optimistic)
                    : (availabilityResults[employee.id]?.isAvailable ?? true);
                  const isUnavailable = showAvailabilityIndicator && !loadingAvailability && !isAvailable;
                  const initials = employee.name
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase();

                  // Generate consistent color from employee name
                  const avatarColors = [
                    'bg-red-500 text-white',
                    'bg-orange-500 text-white',
                    'bg-amber-500 text-white',
                    'bg-yellow-500 text-black',
                    'bg-lime-500 text-black',
                    'bg-green-500 text-white',
                    'bg-emerald-500 text-white',
                    'bg-teal-500 text-white',
                    'bg-cyan-500 text-black',
                    'bg-sky-500 text-white',
                    'bg-blue-500 text-white',
                    'bg-indigo-500 text-white',
                    'bg-violet-500 text-white',
                    'bg-purple-500 text-white',
                    'bg-fuchsia-500 text-white',
                    'bg-pink-500 text-white',
                    'bg-rose-500 text-white',
                  ];
                  const colorIndex = employee.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % avatarColors.length;
                  const avatarColor = avatarColors[colorIndex];

                  return (
                    <SelectItem
                      key={employee.id}
                      value={employee.id}
                      disabled={isUnavailable}
                      hideIndicator
                      className={cn(
                        'py-1',
                        isUnavailable && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <span className="flex items-center gap-1.5 text-sm w-full">
                        <Avatar className="h-5 w-5 flex-shrink-0">
                          <AvatarFallback className={cn("text-[10px] font-medium", avatarColor)}>
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <span className="flex-1 text-right truncate">{employee.name}</span>
                        {showAvailabilityIndicator && !isAvailable && !loadingAvailability && (
                          <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
              </>
            )}
          </SelectContent>
        </Select>
      );

    case 'simple':
    default:
      return (
        <Select
          value={config.mode === 'single' ? (selectedEmployees[0] || 'unassigned') : ''}
          onValueChange={(value) => {
            const newValue = value === 'unassigned' ? null : value;
            if (onValueChange) {
              onValueChange(newValue);
            }
          }}
          onOpenChange={handleOpenChange}
          disabled={disabled || loading}
        >
          <SelectTrigger 
            className={cn('w-full', triggerClassName)}
            style={triggerStyle}
          >
            <SelectValue>
              {loading ? (
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Loading...
                </span>
              ) : renderTrigger ? (
                renderTrigger(selectedEmployeesData, placeholder)
              ) : selectedEmployeesData.length > 0 ? (
                selectedEmployeesData[0].name
              ) : (
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {placeholder}
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className={contentClassName}>
            {config.includeUnassigned !== false && (
              <SelectItem value="unassigned" className="text-muted-foreground">
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Unassigned
                </span>
              </SelectItem>
            )}
            {loading ? (
              <div className="py-4">
                <LoadingSkeleton variant="form" rows={3} />
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              <>
                {loadingAvailability && showAvailabilityIndicator && (
                  <div className="py-2 px-3 text-sm text-muted-foreground text-center">
                    Checking availability...
                  </div>
                )}
                {filteredEmployees.map((employee) => {
                  const isAvailable = loadingAvailability
                    ? false  // Not available during loading - must verify first
                    : (availabilityResults[employee.id]?.isAvailable ?? false);
                  const isUnavailable = showAvailabilityIndicator && (loadingAvailability || !isAvailable);

                  return (
                    <SelectItem
                      key={employee.id}
                      value={employee.id}
                      disabled={isUnavailable}
                      className={cn(
                        isUnavailable && 'opacity-50 cursor-not-allowed',
                        loadingAvailability && 'animate-pulse'
                      )}
                    >
                    {renderEmployee ? (
                      renderEmployee(employee, isEmployeeSelected(employee.id))
                    ) : (
                      <EmployeeDisplay
                        employee={employee as StandardEmployeeSelection}
                        isSelected={isEmployeeSelected(employee.id)}
                        showRoleInfo={showRoleInfo}
                        showStatusBadge={showStatusBadge}
                        showAvailabilityIndicator={showAvailabilityIndicator}
                        available={isAvailable}
                        conflicts={availabilityResults[employee.id]?.conflicts ?? []}
                        refreshing={refreshingAvailability}
                      />
                    )}
                  </SelectItem>
                );
              })}
              </>
            )}
          </SelectContent>
        </Select>
      );
  }
}